import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FormControlLabel, Switch, Paper } from "@mui/material";
import { TableSetup } from "../components/TableSetup";
import { Header } from "../components/Header";
import { SessionInfoCard } from "../components/SessionInfoCard";
import { useTrainingRoomAccess } from "../hooks/useTrainingRoomAccess";
import { usePermissions } from "../context/Permissioncontext";
import { TrainingCoachFloatingBar } from "../coaching/TrainingCoachFloatingBar";
import { SessionReadOnlyBanner } from "../components/SessionReadOnlyBanner";
import {
  readAutoAdvanceSequences,
  writeAutoAdvanceSequences,
} from "../utils/trainingSequencePreferences";
import { isTeSequences } from "../utils/teExecutionType";
import { persistLiveTrainingSession } from "../hooks/useLiveTrainingRedirect";
import { primeEvalCameraPermission } from "../utils/evalCameraPermission";
import { requestLiveFullscreen } from "../hooks/useLiveFullscreen";
import { isMobileDevice } from "../utils/isMobileDevice";
import {
  shouldCoachAutoEnterLiveTraining,
  ensureCoachRoleForLiveTrainingEntry,
} from "../utils/liveTrainingNav";
import { useTourDemoActive } from "../tour/useTourDemoActive";
import { demoHomeGroups, demoSession } from "../tour/tourDemoFixtures";
import "./TrainingTheme.css";

const TRAINING_SCROLL_STORAGE_KEY = "smarttt_training_scroll_v1";

export function Training() {
  const navigate = useNavigate();
  const [selectedSession, setSelectedSession] = useState(
    JSON.parse(localStorage.getItem("rsessn"))
  );
  const selectedSessionRef = useRef(selectedSession);
  selectedSessionRef.current = selectedSession;
  const prevTsIdForScrollRef = useRef(undefined);
  const [selectedgrp, setSelectedgrp] = useState(
    JSON.parse(localStorage.getItem("grpinf"))
  );
  const { t } = useTranslation();
  const tourDemo = useTourDemoActive();
  const displaySession = selectedSession || (tourDemo ? demoSession() : null);
  const displayGrp = selectedgrp || (tourDemo ? demoHomeGroups()[0] : selectedgrp);
  const { isCoachView, sessionUser } = useTrainingRoomAccess();
  const { permissions } = usePermissions();
  const [coachLiveContext, setCoachLiveContext] = useState(null);
  const handleCoachLiveContext = useCallback((ctx) => {
    setCoachLiveContext(ctx);
  }, []);

  useEffect(() => {
    if (!isCoachView) {
      setCoachLiveContext(null);
    }
  }, [isCoachView]);

  useEffect(() => {
    if (!selectedSession?.ts_id) {
      setCoachLiveContext(null);
    }
  }, [selectedSession?.ts_id]);

  const [sessionExercisesSnapshot, setSessionExercisesSnapshot] = useState([]);
  const [trainingSelectedExercise, setTrainingSelectedExercise] = useState(null);
  const [autoAdvanceSequences, setAutoAdvanceSequences] = useState(false);
  const handleExercisesSnapshot = useCallback((list) => {
    setSessionExercisesSnapshot(Array.isArray(list) ? list : []);
  }, []);
  const handleSelectedExerciseChange = useCallback((ex) => {
    setTrainingSelectedExercise(ex || null);
  }, []);

  const showAutoAdvanceSequencesPref =
    isCoachView &&
    selectedSession?.ts_id &&
    (!trainingSelectedExercise || isTeSequences(trainingSelectedExercise));

  // Within 30 min of start (or already live): Training becomes Live Training.
  useEffect(() => {
    if (tourDemo || !isCoachView) return undefined;
    const session = selectedSession;
    if (!shouldCoachAutoEnterLiveTraining(session)) return undefined;
    let cancelled = false;
    (async () => {
      if (isMobileDevice()) {
        try {
          await primeEvalCameraPermission();
        } catch {
          /* ignore */
        }
        requestLiveFullscreen();
      }
      if (cancelled) return;
      ensureCoachRoleForLiveTrainingEntry(sessionUser, permissions);
      persistLiveTrainingSession(session, selectedgrp);
      navigate("/live-training", { replace: true });
    })();
    return () => {
      cancelled = true;
    };
  }, [
    isCoachView,
    selectedSession,
    selectedgrp,
    sessionUser,
    permissions,
    navigate,
    tourDemo,
  ]);

  useEffect(() => {
    const tsId = selectedSession?.ts_id;
    if (!tsId) {
      setAutoAdvanceSequences(true);
      return;
    }
    setAutoAdvanceSequences(readAutoAdvanceSequences(tsId));
  }, [selectedSession?.ts_id]);

  // Save scroll only when unmounting /training (not when ts_id changes) so the stored id matches the session user left.
  useEffect(() => {
    return () => {
      const tsId = selectedSessionRef.current?.ts_id;
      if (!tsId) return;
      try {
        sessionStorage.setItem(
          TRAINING_SCROLL_STORAGE_KEY,
          JSON.stringify({ tsId, y: window.scrollY })
        );
      } catch {
        // ignore
      }
    };
  }, []);

  useLayoutEffect(() => {
    const tsId = selectedSession?.ts_id;
    if (!tsId) {
      prevTsIdForScrollRef.current = null;
      return;
    }

    const prev = prevTsIdForScrollRef.current;

    if (prev && prev !== tsId) {
      window.scrollTo(0, 0);
      try {
        sessionStorage.removeItem(TRAINING_SCROLL_STORAGE_KEY);
      } catch {
        // ignore
      }
      prevTsIdForScrollRef.current = tsId;
      return;
    }

    if (prev === null) {
      window.scrollTo(0, 0);
      try {
        sessionStorage.removeItem(TRAINING_SCROLL_STORAGE_KEY);
      } catch {
        // ignore
      }
      prevTsIdForScrollRef.current = tsId;
      return;
    }

    if (prev === undefined) {
      let mem = null;
      try {
        const raw = sessionStorage.getItem(TRAINING_SCROLL_STORAGE_KEY);
        mem = raw ? JSON.parse(raw) : null;
      } catch {
        mem = null;
      }
      const restore =
        mem?.tsId === tsId && typeof mem.y === "number";
      if (restore) {
        const y = mem.y;
        requestAnimationFrame(() => {
          window.scrollTo(0, y);
        });
      } else {
        window.scrollTo(0, 0);
      }
      try {
        sessionStorage.removeItem(TRAINING_SCROLL_STORAGE_KEY);
      } catch {
        // ignore
      }
    }

    prevTsIdForScrollRef.current = tsId;
  }, [selectedSession?.ts_id]);

  const user = JSON.parse(localStorage.getItem("user"));
  const userName =
    user.user_alias !== null ? user.user_alias : user.user_first_name;

  // Listen for session selection events and localStorage changes
  useEffect(() => {
    const handleSessionSelected = () => {
      try {
        const storedSessionStr = localStorage.getItem("rsessn");
        if (!storedSessionStr) {
          if (selectedSession !== null) {
            setSelectedSession(null);
          }
          return;
        }

        const storedSession = JSON.parse(storedSessionStr);
        if (storedSession && storedSession.ts_id) {
          const sessionGrId = storedSession.gr_id || storedSession._groupId;
          const groupGrId = selectedgrp?.groupDetails?.gr_id;
          if (!groupGrId || !sessionGrId || sessionGrId === groupGrId) {
            if (!selectedSession || selectedSession.ts_id !== storedSession.ts_id) {
              setSelectedSession(storedSession);
            }
          }
        }
      } catch (error) {
        console.error("Error parsing session from localStorage:", error);
      }
    };

    const handleGroupSelected = () => {
      try {
        const storedGroupStr = localStorage.getItem("grpinf");
        if (storedGroupStr) {
          const storedGroup = JSON.parse(storedGroupStr);
          if (storedGroup && storedGroup.groupDetails) {
            setSelectedgrp(storedGroup);
          }
        }
      } catch (error) {
        console.error("Error parsing group from localStorage:", error);
      }
    };

    window.addEventListener("sessionSelected", handleSessionSelected);
    window.addEventListener("storage", handleSessionSelected);
    window.addEventListener("groupSelected", handleGroupSelected);

    handleSessionSelected();
    handleGroupSelected();

    return () => {
      window.removeEventListener("sessionSelected", handleSessionSelected);
      window.removeEventListener("storage", handleSessionSelected);
      window.removeEventListener("groupSelected", handleGroupSelected);
    };
  }, [selectedgrp?.groupDetails?.gr_id, selectedSession?.ts_id]);

  return (
    <>
      <div
        id="adminRoot"
        className="coach-session-page training-theme-root"
        style={{
          paddingBottom: "80px",
          paddingTop:
            isCoachView &&
            selectedSession?.ts_id &&
            coachLiveContext?.exercises?.length
              ? "104px"
              : undefined,
          zoom: 0.92,
          minHeight: "100vh",
          paddingLeft: "12px",
          paddingRight: "12px",
          backgroundColor: "var(--bg-color)",
        }}
      >
        <Header
          userRole={user.type_user_id}
          user={user}
          element={
            <div className="header-info">
              <h3>
                {t("welcome")} {userName}
              </h3>
              <p>{t("training")}</p>
            </div>
          }
          logo={
            displayGrp?.groupDetails?.gr_image ||
            displayGrp?.groupDetails?.club_image ||
            user?.club_details?.club_image
          }
        />
        {!isCoachView ? <SessionReadOnlyBanner page="training" /> : null}
        <div data-tour-id="coach-training-floor">
        <SessionInfoCard
          selectedSession={displaySession}
          selectedgrp={displayGrp}
          sessionExercisesSnapshot={sessionExercisesSnapshot}
          onExerciseControlStart={
            isCoachView && selectedSession?.ts_id
              ? () =>
                  navigate("/CoachExerciseControl", {
                    state: { session: selectedSession, selectedgrp },
                  })
              : undefined
          }
        />
        </div>
        {showAutoAdvanceSequencesPref ? (
          <Paper
            elevation={0}
            sx={{
              maxWidth: 720,
              mx: "auto",
              mb: 2,
              px: 2,
              py: 1.5,
              borderRadius: 2,
              border: "1px solid var(--border-color)",
              bgcolor: "var(--surface-color)",
            }}
          >
            <FormControlLabel
              control={
                <Switch
                  checked={autoAdvanceSequences}
                  onChange={(_, checked) => {
                    writeAutoAdvanceSequences(selectedSession.ts_id, checked);
                    setAutoAdvanceSequences(checked);
                  }}
                  color="primary"
                />
              }
              label={
                <span>
                  <strong>{t("autoAdvanceSequences") || "Auto-start sequences"}</strong>
                  <span style={{ display: "block", fontSize: 13, fontWeight: 400, color: "var(--text-secondary)", marginTop: 4 }}>
                    {t("autoAdvanceSequencesHint") ||
                      "When off, start each following block from Exercise Control after the current block ends."}
                  </span>
                </span>
              }
              sx={{ alignItems: "flex-start", m: 0 }}
            />
          </Paper>
        ) : null}
        {selectedSession ? (
          <TableSetup
            selectedSession={selectedSession}
            selectedgrp={selectedgrp}
            onExercisesSnapshot={handleExercisesSnapshot}
            onSelectedExerciseChange={handleSelectedExerciseChange}
            onCoachLiveContext={
              isCoachView ? handleCoachLiveContext : undefined
            }
            onSessionLiveStatusUpdate={(patch) => {
              setSelectedSession((prev) => {
                if (!prev?.ts_id) return prev;
                const next = { ...prev, ...patch };
                if (patch.te_state != null) {
                  next.tbl_state_te = {
                    ...(prev.tbl_state_te || {}),
                    st_id: patch.te_state,
                  };
                }
                try {
                  localStorage.setItem("rsessn", JSON.stringify(next));
                } catch {
                  // ignore
                }
                window.dispatchEvent(new Event("sessionSelected"));
                return next;
              });
            }}
          />
        ) : tourDemo ? null : (
          <div style={{ 
            padding: "40px 20px", 
            textAlign: "center", 
            color: "#666",
            fontSize: "16px"
          }}>
            {t("noSessionSelected") || "Please select a training session from the Home page to view table setup"}
          </div>
        )}
      </div>
      <TrainingCoachFloatingBar
        visible={isCoachView && !!selectedSession?.ts_id}
        coachLiveContext={coachLiveContext}
        selectedSession={selectedSession}
        selectedgrp={selectedgrp}
      />
    </>
  );
}
